import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { grafanaApiRef } from '../api/grafanaApi';

interface DataSourceContextType {
  selectedDataSource: string | null;
  setSelectedDataSource: (dataSource: string | null) => void;
  dataSources: Array<{ uid: string; name: string }>;
  loading: boolean;
  error: Error | null;
}

const DataSourceContext = createContext<DataSourceContextType>({
  selectedDataSource: null,
  setSelectedDataSource: () => {},
  dataSources: [],
  loading: true,
  error: null,
});

export const useDataSourceContext = () => useContext(DataSourceContext);

interface DataSourceProviderProps {
  children: ReactNode;
}

export const DataSourceProvider = ({ children }: DataSourceProviderProps) => {
  const [selectedDataSource, setSelectedDataSource] = useState<string | null>(
    null,
  );
  const [dataSources, setDataSources] = useState<
    Array<{ uid: string; name: string }>
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const grafanaApi = useApi(grafanaApiRef);

  useEffect(() => {
    const fetchDataSources = async () => {
      try {
        setLoading(true);
        const sources = await grafanaApi.getDataSources();

        // Mapear para o formato simplificado
        const mappedSources = sources.map((ds: any) => ({
          uid: ds.uid,
          name: ds.name,
        }));

        setDataSources(mappedSources);

        // Pré-selecionar o primeiro banco de dados se houver algum
        if (mappedSources.length > 0 && !selectedDataSource) {
          setSelectedDataSource(mappedSources[0].uid);
        }

        setLoading(false);
      } catch (err) {
        setError(err as Error);
        setLoading(false);
      }
    };

    fetchDataSources();
    // selectedDataSource é lido para evitar sobrescrever escolha do usuário,
    // mas o fetch deve rodar uma única vez por montagem do provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grafanaApi]);

  return (
    <DataSourceContext.Provider
      value={{
        selectedDataSource,
        setSelectedDataSource,
        dataSources,
        loading,
        error,
      }}
    >
      {children}
    </DataSourceContext.Provider>
  );
};
